import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Package, Store, Truck, ChevronLeft, Clock, X, Upload, Download, Check } from "lucide-react";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";

const badge = (s) => ["delivered", "completed"].includes(s) ? "bg-emerald-100 text-emerald-700" : ["cancelled", "refunded", "returned"].includes(s) ? "bg-rose-100 text-rose-700" : s === "on_hold" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700";
const label = (s) => (s || "").replace(/_/g, " ");
const RETURN_REASONS = ["Damaged / defective", "Wrong item received", "Item not as described", "Missing parts or accessories", "Size or fit issue", "Quality not satisfactory", "Other"];

const REFUND_STEPS = [{ key: "pending", label: "Pending" }, { key: "initiated", label: "Initiated" }, { key: "confirmed", label: "Confirmed" }];
const REFUND_INDEX = { refund_pending: 0, refund_initiated: 1, refund_processing: 1, refunded: 2 };

function RefundTracker({ status, amount }) {
  if (status === "reconciliation_required") return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="refund-tracker-recon">
      <span className="font-semibold">Refund needs reconciliation.</span> We're verifying this refund with the payment provider and will email you the moment it's confirmed.
    </div>
  );
  const reached = REFUND_INDEX[status];
  if (reached == null) return null;
  return (
    <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3" data-testid="refund-tracker" data-status={status}>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold text-emerald-700">Refund progress</p>
        {amount != null && <p className="text-xs font-bold text-emerald-700">₹{amount}</p>}
      </div>
      <div className="flex items-center">
        {REFUND_STEPS.map((st, i) => {
          const done = i <= reached, current = i === reached;
          return (
            <div key={st.key} className="flex flex-1 items-center last:flex-none" data-testid={`refund-step-${st.key}`} data-state={current ? "current" : done ? "done" : "todo"}>
              <div className="flex flex-col items-center">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${done ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-400"} ${current ? "ring-2 ring-emerald-300 ring-offset-1" : ""}`}>
                  {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className={`mt-1 text-[10px] font-medium ${done ? "text-emerald-700" : "text-slate-400"}`}>{st.label}</span>
              </div>
              {i < REFUND_STEPS.length - 1 && <span className={`mx-1 mb-4 h-0.5 flex-1 rounded ${i < reached ? "bg-emerald-400" : "bg-slate-200"}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RequestModal({ sub, mode, onClose }) {
  const [reason, setReason] = useState(RETURN_REASONS[0]);
  const [details, setDetails] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const isReturn = mode === "return";

  const submit = async () => {
    setBusy(true);
    const full = `${reason}${details.trim() ? ` — ${details.trim()}` : ""}`;
    try {
      await api.post(`/marketplace/orders/${sub.suborder_id}/${isReturn ? "return" : "replacement"}`, { reason: full });
      if (isReturn && files.length) {
        for (const f of files) {
          const fd = new FormData(); fd.append("file", f);
          try { await api.post(`/marketplace/orders/${sub.suborder_id}/return/evidence`, fd); } catch (e) { console.error("Return evidence upload failed for a file, continuing:", e); }
        }
      }
      toast.success(isReturn ? "Return requested" : "Replacement requested");
      window.dispatchEvent(new Event("account-updated"));
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="request-modal" onClick={() => onClose(false)}>
      <div className="max-h-[85vh] w-full max-w-md overflow-auto rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <p className="text-base font-semibold text-slate-800">{isReturn ? "Request a return" : "Request a replacement"}</p>
          <button onClick={() => onClose(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <label className="block text-xs font-medium text-slate-500">Reason
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="request-reason" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
            {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select></label>
        <label className="mt-3 block text-xs font-medium text-slate-500">More details (optional)
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} data-testid="request-details" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Tell the seller what went wrong" /></label>
        {isReturn && (
          <div className="mt-3">
            <label className="text-xs font-medium text-slate-500">Photos (optional, helps the seller)</label>
            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-500 hover:border-blue-400" data-testid="request-images">
              <Upload className="h-4 w-4" /> Add images
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </label>
            {files.length > 0 && <p className="mt-1 text-[11px] text-slate-500" data-testid="request-image-count">{files.length} image(s) selected</p>}
          </div>
        )}
        <button onClick={submit} disabled={busy} data-testid="request-submit" className="mt-4 w-full rounded-full brand-gradient-bg py-2.5 text-sm font-semibold text-white disabled:opacity-60">{busy ? "Submitting…" : (isReturn ? "Submit return request" : "Submit replacement request")}</button>
      </div>
    </div>
  );
}

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState(null); // { sub, mode }

  const load = () => api.get("/marketplace/my-orders").then((r) => {
    setOrder((r.data.orders || []).find((o) => o.order_id === orderId || (o.suborders || []).some((s) => s.suborder_id === orderId)) || null); setLoaded(true);
  }).catch(() => setLoaded(true));
  useEffect(() => { load(); }, [orderId]);

  if (!loaded) return null;
  if (!order) return (
    <div className="p-10 text-center" data-testid="order-not-found">
      <Package className="mx-auto h-8 w-8 text-slate-300" />
      <p className="mt-3 font-semibold text-slate-700">Order not found</p>
      <button onClick={() => nav("/app/shop-orders")} className="mt-4 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white">Back to orders</button>
    </div>
  );

  return (
    <div data-testid="order-detail-page">
      <button onClick={() => nav("/app/shop-orders")} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft className="h-4 w-4" /> Back to orders</button>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900">Order {order.number}</h1>
          <p className="text-xs text-slate-400">{new Date(order.created_at).toLocaleString()} · {order.suborders?.length} seller(s)</p>
        </div>
        <p className="text-lg font-bold text-slate-900">₹{order.total}</p>
      </div>

      <div className="mt-4 space-y-4">
        {(order.suborders || []).map((s) => (
          <div key={s.suborder_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`detail-suborder-${s.suborder_id}`}>
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-700"><Store className="h-3.5 w-3.5 text-blue-500" /> {s.items?.[0]?.seller?.name || "Seller"} · <span className="text-slate-400">{s.number}</span></span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${badge(s.status)}`}>{label(s.status)}</span>
            </div>
            <div className="mt-3 space-y-2">
              {s.items.map((it) => (
                <div key={it.product_id} className="flex items-center gap-2 text-sm text-slate-600">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">{it.image ? <img src={it.image} alt={it.name} className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-slate-300" />}</span>
                  <span className="min-w-0 flex-1"><span className="block truncate font-medium text-slate-800">{it.name}</span><span className="text-xs text-slate-400">Qty {it.qty}</span></span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {it.price != null && <span className="font-semibold text-slate-800">₹{it.price}</span>}
                    {it.mrp > it.price && <span className="text-xs text-slate-400 line-through">₹{it.mrp}</span>}
                  </span>
                </div>
              ))}
            </div>
            {s.shipment && (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                <Truck className="h-3.5 w-3.5 text-blue-500" /> {s.shipment.courier} · AWB <b>{s.shipment.awb}</b> · <span className="capitalize">{label(s.shipment.shipping_status)}</span>
              </div>
            )}
            {s.delivery_otp && !["delivered", "completed", "cancelled", "refunded", "returned"].includes(s.status) && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid={`delivery-code-${s.suborder_id}`}>
                <p className="text-xs font-semibold text-amber-800">Delivery handover code</p>
                <p className="mt-0.5 text-2xl font-extrabold tracking-[0.3em] text-amber-900" data-testid={`delivery-code-value-${s.suborder_id}`}>{s.delivery_otp}</p>
                <p className="mt-1 text-[11px] text-slate-500">Share this code with the delivery agent only when the parcel is in your hand.</p>
              </div>
            )}
            {(s.tracking_history || []).length > 0 && (
              <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2" data-testid={`detail-timeline-${s.suborder_id}`}>
                {(s.tracking_history || []).map((h, i) => (
                  <div key={`${h.at}-${i}`} className="text-xs"><span className="font-medium capitalize text-slate-700">{label(h.status)}</span> — <span className="text-slate-500">{h.description}</span>
                    <span className="block text-[10px] text-slate-400"><Clock className="mr-1 inline h-2.5 w-2.5" />{new Date(h.at).toLocaleString()} · {label(h.source)}</span></div>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button onClick={() => downloadFile(`/marketplace/orders/${s.suborder_id}/tax-invoice.pdf`, `${s.number}-tax-invoice.pdf`)} data-testid={`detail-invoice-${s.suborder_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-blue-400"><Download className="h-3.5 w-3.5" /> Invoice</button>
              {["delivered", "completed"].includes(s.status) && !s.refund_status && (
                <>
                  <button onClick={() => setModal({ sub: s, mode: "return" })} data-testid={`detail-return-${s.suborder_id}`} className="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">Request return</button>
                  <button onClick={() => setModal({ sub: s, mode: "replacement" })} data-testid={`detail-replace-${s.suborder_id}`} className="rounded-full border border-purple-200 px-3 py-1.5 text-xs font-semibold text-purple-600 hover:bg-purple-50">Request replacement</button>
                </>
              )}
              {s.status === "return_requested" && <span className="text-xs text-amber-600">Return requested</span>}
            </div>
            {s.refund_status && <RefundTracker status={s.refund_status} amount={s.return?.amount ?? s.net_amount} />}
          </div>
        ))}
      </div>

      {modal && <RequestModal sub={modal.sub} mode={modal.mode} onClose={(ok) => { setModal(null); if (ok) load(); }} />}
    </div>
  );
}
